// =============================================================================
// Core scalar types
// =============================================================================

/** CIE L*a*b* triplet */
export type Lab = [number, number, number];

/** sRGB triplet 0–255 */
export type RGB = [number, number, number];

/** CMY ink levels — derived from RGB as [1-R/255, 1-G/255, 1-B/255] */
export type CMY = [number, number, number];

/** XYZ under D50 (absolute) */
export type XYZ = [number, number, number];

// =============================================================================
// Result monad  (used throughout the engine — never throw)
// =============================================================================

export type Result<T, E = string> =
  | { ok: true;  value: T }
  | { ok: false; error: E };

export function ok<T>(value: T): Result<T, never>  { return { ok: true,  value }; }
export function err<E>(error: E): Result<never, E> { return { ok: false, error }; }

// =============================================================================
// Patch group identifiers
// =============================================================================

export type PatchGroupId =
  | 'R' | 'G' | 'B'           // single-channel ramps (in CMY: C / M / Y vary)
  | 'RG' | 'RB' | 'GB'        // binary overprints    (CM / CY / MY pairs)
  | 'RGB'                       // 3-ink (CMY) overprint ramp — R=G=B decrease together
  | 'N'                         // neutral axis (R=G=B) — used for gray balance analysis
  | 'NN32'  | 'NN64'  | 'NN96'
  | 'NN128' | 'NN160' | 'NN192' | 'NN224'; // near-neutral clusters

// =============================================================================
// CGATS parsed output
// =============================================================================

export interface ParsedCGATS {
  originator:   string;
  descriptor:   string;
  created:      string;
  /** All field names in the DATA_FORMAT section */
  fields:       string[];
  numberOfSets: number;
  /** One record per DATA row — numeric values coerced to number, IDs kept as string */
  rows:         Record<string, string | number>[];
  /** Original file text preserved verbatim */
  raw:          string;
}

// =============================================================================
// Measured patch
// =============================================================================

export interface PatchFlags {
  // --- Diagnostic flags (computed by diagnostics.ts) ---
  /** Unexpected hue shift (>3°) between consecutive ink steps in a gamut curve */
  hueAnomaly:      boolean;
  /** Unexpected chroma collapse (>20% drop) in a gamut curve */
  chromaAnomaly:   boolean;
  /** Excluded from working domain Ω — set ONLY by buildWorkingDomain */
  outOfDomain:     boolean;

  // --- Data-quality flags (set by data-quality.ts) ---
  outlier:         boolean;
  interpolated:    boolean;
  extrapolated:    boolean;
  noisy:           boolean;
  repeatsCount:    number;
  repeatStdDev:    number;
}

/**
 * Spectrophotometric reflectance data from a CGATS row.
 * nm[]     — wavelengths in nanometres, sorted ascending (e.g. [380, 390, …, 730])
 * values[] — corresponding reflectance values (0–1 or 0–100)
 */
export interface SpectralData {
  nm:     number[];
  values: number[];
}

export interface MeasuredPatch {
  /** SAMPLE_ID from CGATS, e.g. "R09", "NN128_03" */
  id:       string;
  group:    PatchGroupId;
  /** Device RGB values sent to printer (0–255) */
  rgb:      RGB;
  /** CMY ink levels derived as [1-R/255, 1-G/255, 1-B/255] */
  cmy:      CMY;
  /** Measured Lab (D50/2°) */
  lab:      Lab;
  flags:    PatchFlags;
  /** Spectral reflectance — present when CGATS file contains spectral columns */
  spectral?: SpectralData;
}

// =============================================================================
// Cleaning (data-quality.ts) — unchanged
// =============================================================================

export interface CleaningOptions {
  averageRepeats:        boolean;
  repeatNoiseThreshold:  number;
  outlierMethod:         'residual' | 'iqr' | 'both';
  outlierThresholdDE:    number;
  outlierThresholdIQR:   number;
  autoRemoveOutliers:    boolean;
  interpolateGaps:       boolean;
  extrapolateEdges:      boolean;
  maxExtrapolationSteps: number;
  smoothingMethod:       'none' | 'gaussian' | 'savitzky-golay';
  smoothingSigma:        number;
  smoothOnlyFlagged:     boolean;
}

export interface GroupCleaningReport {
  group:          PatchGroupId;
  inputCount:     number;
  outputCount:    number;
  outliers:       string[];
  interpolated:   string[];
  extrapolated:   string[];
  repeatStdDev:   Record<string, number>;
}

export interface CleaningReport {
  repeatsAveraged:    number;
  outliersDetected:   number;
  outliersRemoved:    number;
  gapsInterpolated:   number;
  gapsExtrapolated:   number;
  noisyPatches:       number;
  perGroup:           Partial<Record<PatchGroupId, GroupCleaningReport>>;
}

export interface CleaningResult {
  patches: MeasuredPatch[];
  report:  CleaningReport;
}

// =============================================================================
// Diagnostics — CMY subtractive model
// =============================================================================

/** CMY gamut label for a primary, binary-overprint, or 3-ink overprint curve */
export type GamutLabel = 'C' | 'M' | 'Y' | 'CM' | 'CY' | 'MY' | 'CMY';

export interface GamutPoint {
  patchId:      string;
  /**
   * Axis parameter — distance along the RGB-cube axis from the paper-white corner.
   * Always ∈ [0, 1] for all curve types (same scale as WorkingDomain limits):
   *   C / M / Y curves    → single channel (C, M, or Y)
   *   CM / CY / MY curves → average of the two active channels, e.g. (C+M)/2
   *   CMY curve           → average of all three: (C+M+Y)/3
   */
  ink:          number;
  a:            number;
  b:            number;
  L:            number;
  hueAngle:     number;   // atan2(b, a) in degrees [0, 360)
  chroma:       number;   // sqrt(a²+b²)
  dHue:         number;   // Δhue vs previous point (0 for first)
  dChroma:      number;   // Δchroma vs previous point (0 for first)
  flags: {
    hueAnomaly:    boolean;
    chromaAnomaly: boolean;
  };
}

export interface GamutCurve {
  label:      GamutLabel;
  points:     GamutPoint[];
  maxChroma:  number;
  maxInk:     number;
  /** a*, b*, L* of highest-ink point — the device's gamut boundary for this primary */
  gamutPoint: { a: number; b: number; L: number };
}

export interface GamutDiagnostic {
  curves: {
    C:  GamutCurve;
    M:  GamutCurve;
    Y:  GamutCurve;
    CM: GamutCurve;
    CY: GamutCurve;
    MY: GamutCurve;
    /** Present only when RGB-group patches exist in the measured target */
    CMY?: GamutCurve;
  };
}

export interface DeviceAnchors {
  white: { L: number; a: number; b: number };  // Lab at CMY=(0,0,0) = RGB=(255,255,255)
  black: { L: number; a: number; b: number };  // Lab at CMY=(1,1,1) = RGB=(0,0,0)
}

/** Near-neutral Jacobian for one gray level */
export interface NNSensitivity {
  level:  number;  // 32 | 64 | 96 | 128 | 160 | 192 | 224
  center: { L: number; a: number; b: number };
  dAdR: number;  // da*/dR
  dAdB: number;  // da*/dB
  dBdR: number;  // db*/dR
  dBdB: number;  // db*/dB
  /** det of Jacobian (near 0 = ill-conditioned) */
  det:    number;
}

export interface GrayBalanceStep {
  patchId:  string;
  /** G channel value 0–255 (neutral reference) */
  gRgb:     number;
  /** M ink = 1 − G/255 */
  mInk:     number;
  L:        number;
  /** a* of N patch − paper a* */
  aCast:    number;
  /** b* of N patch − paper b* */
  bCast:    number;
  /** sqrt(aCast²+bCast²) */
  deltaE:   number;
  /** ΔR = R_for_gray − G  (null when Jacobian ill-conditioned) */
  dRFromG:  number | null;
  /** ΔB = B_for_gray − G */
  dBFromG:  number | null;
  /** ΔC ink = −ΔR/255 */
  dCInk:    number | null;
  /** ΔY ink = −ΔB/255 */
  dYInk:    number | null;
}

export interface GrayBalanceDiagnostic {
  paperWhite:    { L: number; a: number; b: number };
  steps:         GrayBalanceStep[];
  maxCast:       number;
  dominantCast:  'warm' | 'cool' | 'neutral';
}

export interface DiagnosticReport {
  gamut:            GamutDiagnostic;
  grayBalance:      GrayBalanceDiagnostic;
  /** Lab of the brightest N patch (paper white = CMY=0,0,0) */
  paperWhite:       { L: number; a: number; b: number };
  /** Lab of the darkest N patch (paper black = CMY=1,1,1) */
  paperBlack:       { L: number; a: number; b: number };
  /** Both CMY cube corner anchors, used by LUT builder */
  anchors:          DeviceAnchors;
  /** Union of hueAnomaly + chromaAnomaly patch IDs (NOT outOfDomain) */
  flaggedPatchIds:  string[];
  /** Auto-suggested domain from gamut curve analysis */
  recommendedDomain: WorkingDomain;
}

// =============================================================================
// Working domain Ω — all parameters in CMY ink units (0–1 per primary)
// =============================================================================

export interface WorkingDomain {
  /** Max cyan   ink 0–1  (→ R_min = round((1-cMax)·255)) */
  cMax:   number;
  /** Max magenta ink 0–1 */
  mMax:   number;
  /** Max yellow  ink 0–1 */
  yMax:   number;
  /** Max per-channel ink on CM axis 0–1  (excluded when C > cmMax AND M > cmMax) */
  cmMax:  number;
  /** Max per-channel ink on CY axis 0–1  (excluded when C > cyMax AND Y > cyMax) */
  cyMax:  number;
  /** Max per-channel ink on MY axis 0–1  (excluded when M > myMax AND Y > myMax) */
  myMax:  number;
  /** Max per-channel ink on CMY diagonal 0–1  (excluded when C,M,Y all > cmyMax) */
  cmyMax: number;
  /** True when the user has manually overridden the suggested limits */
  userOverride: boolean;
}

// =============================================================================
// 3D LUT
// =============================================================================

export type InterpolationMethod = 'trilinear' | 'tetrahedral';

export interface LUTOptions {
  gridSize:                  17 | 33;
  interpolation:             InterpolationMethod;
  smoothing:                 number;
  maxMonotonicityIterations: number;
}

export interface LUTValidation {
  monotonic:          boolean;
  nonMonotonicCount:  number;
  maxError:           number;
  meanError:          number;
  neutralDeltaEMax:   number;
  neutralDeltaEMean:  number;
}

export interface LUT3D {
  gridSize:     17 | 33;
  /** Flat Float32Array, layout: R slowest, B fastest. 3 floats per node (0–255). */
  data:         Float32Array;
  domain:       WorkingDomain;
  options:      LUTOptions;
  buildTimeMs:  number;
  validation:   LUTValidation | null;
}

// =============================================================================
// ICC Profile Metadata
// =============================================================================

export interface ProfileMeta {
  description: string;
  copyright:   string;
  creator:     string;
  deviceModel: string;
  mediaType:   string;
  printMode:   string;
  created:     Date;
}

// =============================================================================
// Export
// =============================================================================

export interface ExportOptions {
  formats: ('icc' | 'json' | 'csv' | 'pdf')[];
  gridSize: 17 | 33;
  includeMeasurements: boolean;
}

// =============================================================================
// Application session (Zustand store root)
// =============================================================================

export type AppView = 'import' | 'diagnose' | 'domain' | 'build' | 'export' | 'apply';
export type StepStatus = 'idle' | 'ready' | 'running' | 'done' | 'error';

export interface CalibrationSession {
  id:        string;
  createdAt: string;
  updatedAt: string;

  rawCGATS:        ParsedCGATS | null;
  rawPatches:      MeasuredPatch[] | null;

  cleaningOptions: CleaningOptions;
  cleaningReport:  CleaningReport | null;
  patches:         MeasuredPatch[] | null;

  diagnostics:     DiagnosticReport | null;
  domain:          WorkingDomain | null;

  lutOptions:      LUTOptions;
  lut:             LUT3D | null;

  profileMeta:     ProfileMeta | null;
  deviceLink:      Uint8Array | null;
  validation:      LUTValidation | null;

  stepStatus: {
    import:   StepStatus;
    diagnose: StepStatus;
    domain:   StepStatus;
    build:    StepStatus;
    export:   StepStatus;
    apply:    StepStatus;
  };
}

// =============================================================================
// App errors
// =============================================================================

export type AppErrorCode =
  | 'CGATS_PARSE_ERROR'
  | 'MISSING_LAB_DATA'
  | 'MISSING_RGB_DATA'
  | 'INSUFFICIENT_PATCHES'
  | 'MISSING_BLACK_POINT'
  | 'ANCHOR_VIOLATION'
  | 'LUT_BUILD_FAILED'
  | 'MONOTONICITY_FAILED'
  | 'ICC_WRITE_FAILED'
  | 'EXPORT_FAILED';

export interface AppError {
  code:        AppErrorCode;
  message:     string;
  details?:    unknown;
  recoverable: boolean;
}

// =============================================================================
// Worker messages (lut-builder worker)
// =============================================================================

export interface LUTWorkerRequest {
  type:    'build';
  patches: MeasuredPatch[];
  domain:  WorkingDomain;
  options: LUTOptions;
  anchors: DeviceAnchors;
}

export interface LUTWorkerProgress { type: 'progress'; percent: number; message: string; }
export interface LUTWorkerDone     { type: 'done'; lut: Omit<LUT3D,'data'> & { data: ArrayBuffer }; buildTimeMs: number; }
export interface LUTWorkerError    { type: 'error'; message: string; }
export type LUTWorkerResponse = LUTWorkerProgress | LUTWorkerDone | LUTWorkerError;

// =============================================================================
// Defaults (exported for tests + store init)
// =============================================================================

export const DEFAULT_PATCH_FLAGS: PatchFlags = {
  hueAnomaly: false, chromaAnomaly: false, outOfDomain: false,
  outlier: false, interpolated: false, extrapolated: false, noisy: false,
  repeatsCount: 1, repeatStdDev: 0,
};

/** Working domain with all limits at maximum (1.0) — zero excluded patches. */
export const DEFAULT_WORKING_DOMAIN: WorkingDomain = {
  cMax: 1.0, mMax: 1.0, yMax: 1.0,
  cmMax: 1.0, cyMax: 1.0, myMax: 1.0,
  cmyMax: 1.0,
  userOverride: false,
};

export const DEFAULT_CLEANING_OPTIONS: CleaningOptions = {
  averageRepeats: true,
  repeatNoiseThreshold: 0.8,
  outlierMethod: 'both',
  outlierThresholdDE: 2.5,
  outlierThresholdIQR: 2.0,
  autoRemoveOutliers: false,
  interpolateGaps: true,
  extrapolateEdges: true,
  maxExtrapolationSteps: 2,
  smoothingMethod: 'gaussian',
  smoothingSigma: 0.8,
  smoothOnlyFlagged: false,
};

export const DEFAULT_LUT_OPTIONS: LUTOptions = {
  gridSize: 17,
  interpolation: 'tetrahedral',
  smoothing: 1,   // 1 = full correction; 0 = identity (pass-through)
  maxMonotonicityIterations: 3,
};
